import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, useForm } from "react-hook-form";

import { MessageResponseDto } from "@api/api";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { useQyCreateProviderType } from "@factory/query/provider-type.query";
import {
  ProviderTypeFormRule,
  ProviderTypeFormSchema,
} from "@factory/form/form.rule";
import { providerTypeFormDefault } from "@factory/form/form.default";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useUserIdentityContext } from "@core/services/user-identity.context";

export function useNewProviderType() {
  // LOCAL DECLARATION
  const { getUserRole } = useUserIdentityContext();
  const navigate = useNavigate();
  const { mapCreateProviderType } = useFormApiMapper();
  const { showError, showSuccess, showWarning } = useNotificationContext();

  // QUERIES
  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;

    showSuccess(`New ProviderType is created`);
    navigate(`../${data.code}`);
  };
  const { mutate: addProviderType, isLoading: isCreating } =
    useQyCreateProviderType(handleApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<ProviderTypeFormSchema>({
    defaultValues: providerTypeFormDefault,
    resolver: zodResolver(ProviderTypeFormRule),
  });
  const { handleSubmit } = formMethod;

  const handleValidate = (form: ProviderTypeFormSchema) => {
    const { tenant } = getUserRole();
    const updatedForm = {
      ...form,
      tenant,
    };
    const formData = mapCreateProviderType(updatedForm);
    addProviderType(formData);
  };
  const handleValidateError = (err: FieldErrors<ProviderTypeFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  return {
    isCreating,
    formMethod,
    navigate,
    handleSubmit,
    handleValidate,
    handleValidateError,
  };
}
